import { Sparkles, Loader2 } from "lucide-react";
import { NETWORKS, OBJECTIVES, BACKGROUND_MODES } from "./helpers";

const inputCls =
  "w-full rounded-xl bg-[#111111] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#FF8A00]/60 transition";
const labelCls = "block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5 font-semibold";

export const StrategyForm = ({ value, onChange, onGenerate, loading, progress }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const toggleNetwork = (id) => {
    const has = value.networks.includes(id);
    const next = has ? value.networks.filter((n) => n !== id) : [...value.networks, id];
    onChange({ ...value, networks: next.length ? next : value.networks });
  };

  return (
    <div data-testid="planner-strategy-form" className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="h-6 w-1 rounded-full bg-gradient-to-b from-[#FF3B1F] via-[#FF8A00] to-[#FFD23F]" />
        <h2 className="font-display text-base font-extrabold tracking-tight">Estrategia</h2>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Desde</label>
          <input data-testid="planner-start-date" type="date" value={value.start_date} onChange={set("start_date")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Hasta</label>
          <input data-testid="planner-end-date" type="date" value={value.end_date} onChange={set("end_date")} className={inputCls} />
        </div>
      </div>

      <div className="mt-4">
        <label className={labelCls}>Redes</label>
        <div className="flex flex-wrap gap-2">
          {NETWORKS.map((n) => {
            const active = value.networks.includes(n.id);
            return (
              <button
                key={n.id}
                type="button"
                data-testid={`planner-network-${n.id}`}
                onClick={() => toggleNetwork(n.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  active
                    ? "border-[#FF8A00]/60 text-white bg-[#FF8A00]/15"
                    : "border-white/10 text-zinc-400 hover:text-white hover:border-white/25"
                }`}
              >
                {n.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className={labelCls}>Posts / semana</label>
          <input
            data-testid="planner-frequency"
            type="number" min={1} max={14}
            value={value.frequency_per_week}
            onChange={(e) => onChange({ ...value, frequency_per_week: e.target.value === "" ? "" : Number(e.target.value) })}
            onBlur={(e) => onChange({ ...value, frequency_per_week: Math.min(14, Math.max(1, Number(e.target.value) || 3)) })}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>% artes estáticas</label>
          <input
            data-testid="planner-static-pct"
            type="number" min={0} max={100} step={10}
            value={value.static_percentage}
            onChange={(e) => onChange({ ...value, static_percentage: Number(e.target.value) })}
            className={inputCls}
          />
        </div>
      </div>

      <div className="mt-4">
        <label className={labelCls}>Objetivo</label>
        <select data-testid="planner-objective" value={value.objective} onChange={set("objective")} className={inputCls}>
          {OBJECTIVES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </div>

      <div className="mt-4">
        <label className={labelCls}>Tema / eje del período</label>
        <textarea
          data-testid="planner-theme"
          rows={3} value={value.theme} onChange={set("theme")}
          placeholder="Ej: posicionar automatización con IA para clínicas dentales en Colombia"
          className={inputCls}
        />
      </div>

      <div className="mt-4">
        <label className={labelCls}>Servicios a destacar</label>
        <input data-testid="planner-services" value={value.services} onChange={set("services")}
          placeholder="Respond.io, Google Ads, funnels" className={inputCls} />
      </div>

      <div className="mt-4">
        <label className={labelCls}>Restricciones</label>
        <input data-testid="planner-restrictions" value={value.restrictions} onChange={set("restrictions")}
          placeholder="No hablar de precios, cero emojis" className={inputCls} />
      </div>

      <div className="mt-4">
        <label className={labelCls}>Modo de fondos</label>
        <select data-testid="planner-bg-mode" value={value.background_mode} onChange={set("background_mode")} className={inputCls}>
          {BACKGROUND_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>

      <button
        type="button"
        data-testid="planner-generate-matrix"
        onClick={onGenerate}
        disabled={loading}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-extrabold text-[#050505] bg-gradient-to-r from-[#FF3B1F] via-[#FF8A00] to-[#FFD23F] hover:opacity-90 disabled:opacity-50 transition"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {loading ? (progress || "Generando matriz…") : "Generar matriz con IA"}
      </button>
      <p className="mt-2 text-[11px] text-zinc-500" data-testid="planner-generate-hint">
        Claude Sonnet 4.5 · genera por bloques (~45s cada uno) para evitar timeouts.
      </p>
    </div>
  );
};
